import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from './ui/Modal';
import { APPROVAL_MODAL_Z } from './ui/approvalLayer';
import OsScroll from './ui/OsScroll';
import './McpTerminalApprovalModal.css';
import { visibleEscapes } from '../utils/visibleEscapes';

/**
 * **The one thing standing between a connected MCP client and a shell on the
 * user's production server.** Everything in this file is a security property
 * rather than styling, so read the rules before changing any of it.
 *
 * 1. **THE COMMAND IS RENDERED IN FULL, VERBATIM.** No `slice()`, no
 *    `truncate`, no ellipsis, anywhere on this path. `AIChatPanel.js:793-795`
 *    renders its own approval card as
 *    `` `Run on ${conn_id}: ${command.slice(0, 120)}` `` while `executeTool`
 *    ran the **unmodified** input — so a user could be shown a benign
 *    119-character prefix and approve an arbitrary tail. Fixed 13.09.2026 in
 *    `aiChat/AiApprovalCard.js`, which now follows these same rules. The `pre` wraps and scrolls; a `max-height` in the
 *    CSS is never a licence to shorten the string.
 * 2. **Control characters and ANSI escapes are shown as visible escapes**, not
 *    interpreted. A command is text to be READ, not a terminal to be painted:
 *    a `\r` that scrolls the visible half of the command out of view, or an
 *    `\x1b[2J` that clears it, would hide exactly the part worth hiding.
 * 3. **Nothing the agent wrote is rendered.** The payload has no `reason`,
 *    `title` or `explanation` field, and the tool schema has no such property,
 *    so there is nothing to render. If a document can inject the command it can
 *    inject the justification, and a card that argues on the attacker's behalf
 *    is worse than no card. The Rust side pins the payload's key set exactly.
 * 4. **Escape and the close button both REFUSE**, and the backdrop does
 *    nothing. A modal that dismissed to *approve* would be catastrophic.
 *    `ui/Modal.js` registers its Escape handler on `document` with capture and
 *    `stopImmediatePropagation`, and a second `document` handler loses on
 *    REGISTRATION ORDER after any remount (31.08.2026) — so this listens on
 *    **`window`**, where capture order is guaranteed by PHASE, and switches
 *    `Modal`'s own handler off with `preventEscapeClose`.
 * 5. **Approve is disarmed for `APPROVE_ARM_DELAY_MS`** after the card appears,
 *    restarting on every new `approvalId`, and Refuse holds initial focus so
 *    Return/Space refuses. A prompt that is clickable the instant it paints is
 *    a prompt a queued click approves — `PasskeyApprovalModal`'s rule, and it
 *    matters more here, because this one can be raised while the user is typing
 *    somewhere else.
 * 6. **"Don't ask again" exists in exactly one shape, and no other** — the
 *    session grant the owner asked for three times (**O42, amended
 *    12.09.2026**). Still nothing per command string, nothing per pattern,
 *    nothing persisted, and nothing pre-ticked: this switch starts OFF on every
 *    single card and is never remembered, which is what separates it from the
 *    `Auto-run` switch O42 was written against. It is offered only beside a
 *    command (never a file write), it names the one server it covers, and the
 *    Rust side bounds it by time AND by command count and forces every command
 *    it waves through into a visible tab. **The trade is you stop clicking so
 *    you watch instead** — never both. `terminal_approval.rs`'s header carries
 *    the full amendment; the test below pins the shapes that stay forbidden.
 * 7. **The visible-tab switch chooses WHERE an approved command runs, never
 *    WHETHER it is asked about**, and it must never grow into the latter. On,
 *    the command runs in a terminal tab the user can watch; off, it runs on the
 *    hidden exec channel exactly as it always has. The card still appears for
 *    every single command either way, Approve is still disarmed, Refuse still
 *    holds focus, and Escape still refuses — so this is not rule 6's
 *    "don't ask again" wearing a different hat. Its label says where, never
 *    "always" or "remember", and the rule 6 test scans the rendered card for
 *    exactly those words.
 *
 * The countdown is cosmetic in the sense that matters: **Rust refuses at 120 s
 * on its own sweeper**, so a closed window, a quit app or a frozen webview
 * still ends in a refusal rather than a live prompt.
 */

/** Approve stays disabled this long after the card appears. */
export const APPROVE_ARM_DELAY_MS = 600;

/** Rule 2 above. Shared with the in-app assistant's approval card. */
export { visibleEscapes };

export default function McpTerminalApprovalModal({ request, onRespond, t }) {
  const [armed, setArmed] = useState(false);
  const [remaining, setRemaining] = useState(0);
  // Rule 7. Rust sends the user's last choice as `defaultVisible`; an older
  // payload without it means the visible tab, which is the safer default in the
  // only sense that matters here — the user sees what ran.
  const [visibleTab, setVisibleTab] = useState(true);
  // Rule 6 as amended. **Always starts OFF, on every card, and is never
  // remembered** — unlike rule 7's switch, whose position Rust stores as the
  // next card's default. A "keep going" that pre-ticks itself is the sticky
  // click the original O42 was written against.
  const [grantSession, setGrantSession] = useState(false);
  const refuseRef = useRef(null);
  const approvalId = request?.approvalId ?? null;

  useEffect(() => {
    if (approvalId == null) return undefined;
    setArmed(false);
    const timer = setTimeout(() => setArmed(true), APPROVE_ARM_DELAY_MS);
    return () => clearTimeout(timer);
  }, [approvalId]);

  // Re-read the default for each new card rather than carrying the previous
  // card's switch position forward: the decision belongs to the command in
  // front of the user, not to the one before it.
  useEffect(() => {
    if (approvalId == null) return;
    setVisibleTab(request?.defaultVisible !== false);
    // Rule 6 as amended: back to OFF for every card, always. There is no
    // `defaultGrant` in the payload and there must never be one.
    setGrantSession(false);
  }, [approvalId, request?.defaultVisible]);

  useEffect(() => {
    if (approvalId == null) {
      setRemaining(0);
      return undefined;
    }
    const tick = () => {
      const left = Math.max(0, Math.ceil(((request?.expiresAt ?? 0) - Date.now()) / 1000));
      setRemaining(left);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [approvalId, request?.expiresAt]);

  useEffect(() => {
    if (approvalId != null) refuseRef.current?.focus();
  }, [approvalId]);

  const refuse = useCallback(() => {
    if (approvalId != null) onRespond({ approvalId, approve: false });
  }, [approvalId, onRespond]);

  const approve = useCallback(() => {
    if (approvalId != null && armed) {
      onRespond({ approvalId, approve: true, visible: visibleTab, grantSession });
    }
  }, [approvalId, armed, onRespond, visibleTab, grantSession]);

  // Rule 4. On `window`, not `document`: capture travels window → document →
  // target, so ordering is guaranteed by phase rather than by which component
  // mounted first.
  useEffect(() => {
    if (approvalId == null) return undefined;
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      event.stopImmediatePropagation();
      event.preventDefault();
      refuse();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [approvalId, refuse]);

  if (!request || approvalId == null) return null;

  const port = Number(request.port) || 22;
  const address = `${request.username ? `${request.username}@` : ''}${request.host || ''}:${port}`;

  return (
    <Modal
      zIndex={APPROVAL_MODAL_Z}
      isOpen
      onClose={refuse}
      title={t('mcp.approvalTitle')}
      subtitle={t('mcp.approvalSubtitle')}
      type="warning"
      size="xl"
      preventBackdropClose
      preventEscapeClose
    >
      <div className="mta-target">
        <span className="mta-target-name">{request.connName || request.connId}</span>
        <span className="mta-target-addr">{address}</span>
      </div>

      {request.file ? (
        <>
          {/* A file write. What makes THIS approval meaningful is the path, the
              size and the digest — the content is previewed on purpose, because
              a card carrying the whole file is the unreadable card rule 1
              exists to prevent. The digest is shown in full: shortening it here
              would need a call rule 1's source scan forbids, and a partial hash
              is worth less than none. */}
          <div className="mta-label">{t('mcp.approvalFileLabel')}</div>
          <OsScroll className="mta-command">
            <pre className="mta-command-text" data-testid="mcp-approval-file-path">
              {visibleEscapes(request.file.path)}
            </pre>
          </OsScroll>
          <div className="mta-note" data-testid="mcp-approval-file-meta">
            {t('mcp.approvalFileMeta', {
              bytes: request.file.bytes,
              sha: request.file.sha256,
            })}
          </div>
          <OsScroll className="mta-command mta-preview">
            <pre className="mta-command-text" data-testid="mcp-approval-file-preview">
              {visibleEscapes(request.file.preview)}
            </pre>
          </OsScroll>
          <div className="mta-note">
            {t('mcp.approvalFilePreviewNote', { bytes: request.file.bytes })}
          </div>
        </>
      ) : (
        <>
          <div className="mta-label">{t('mcp.approvalCommandLabel')}</div>
          {/* Rules 1 and 2. The FULL command, escapes made visible. */}
          <OsScroll className="mta-command">
            <pre className="mta-command-text" data-testid="mcp-approval-command">
              {visibleEscapes(request.command)}
            </pre>
          </OsScroll>
        </>
      )}

      <div className="mta-note">{t('mcp.approvalOutputNote')}</div>
      <div className="mta-note">
        {t('mcp.approvalCountdown')}{' '}
        <span className="mta-countdown">{remaining}s</span>
      </div>

      {/* Rule 7. WHERE it runs, never whether it is asked about.
          NOT shown for a file write: there is no command and no shell, so the
          switch would be inert — and worse than inert, because the verdict
          still carries its value and Rust remembers it as the default for the
          NEXT command. Hidden, `visibleTab` keeps the stored default it was
          initialised with, so approving a file write writes nothing back. */}
      {!request.file && (
        <label className="mta-visible">
          <input
            type="checkbox"
            className="checkbox-brand"
            data-testid="mcp-approval-visible"
            checked={visibleTab}
            onChange={(e) => setVisibleTab(e.target.checked)}
          />
          <span>
            <span className="mta-visible-label">{t('mcp.approvalVisibleTab')}</span>
            <span className="mta-visible-hint">{t('mcp.approvalVisibleTabHint')}</span>
          </span>
        </label>
      )}

      {/* Rule 6, as amended 12.09.2026 on the owner's third asking. Same
          reasons as rule 7 for hiding it on a file write — the grant does not
          cover file writes (`SESSION_GRANT_COVERS_FILES`), so offering it on
          one would promise something the Rust side will not do. The label says
          which server and for how long, because a grant whose scope is not on
          the card is a grant given blind. */}
      {!request.file && (
        <label className="mta-visible mta-grant">
          <input
            type="checkbox"
            className="checkbox-brand"
            data-testid="mcp-approval-grant"
            checked={grantSession}
            onChange={(e) => setGrantSession(e.target.checked)}
          />
          <span>
            <span className="mta-visible-label">
              {t('mcp.approvalGrantSession', { name: request.connName || request.host || '' })}
            </span>
            <span className="mta-visible-hint">{t('mcp.approvalGrantSessionHint')}</span>
          </span>
        </label>
      )}

      <div className="mta-actions">
        <button
          type="button"
          ref={refuseRef}
          className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
          onClick={refuse}
        >
          {t('mcp.approvalRefuse')}
        </button>
        <button
          type="button"
          className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm"
          disabled={!armed}
          onClick={approve}
        >
          {t('mcp.approvalApprove')}
        </button>
      </div>
    </Modal>
  );
}
